"use client";

import { useState } from "react";
import { Link } from "react-router-dom";
import { useCheckout } from "@/hooks/useCheckout";
import { PRICING_PLANS, type PricingPlanId } from "@/lib/billing/plans";
import type { SubscriptionStatus } from "@/lib/billing/plans";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SubscriptionExpiredUpsellProps {
  /**
   * The Polar.sh subscription status that caused the access denial.
   * Used to tailor the headline copy.
   */
  status?: SubscriptionStatus;
  /**
   * When the subscription ended, in ms epoch.
   * Shown as a "your access ended on…" date when provided.
   */
  expiredAt?: number | null;
  /**
   * Route to return to after a successful subscription checkout.
   * Defaults to the current path.
   */
  returnPath?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function headlineForStatus(status: SubscriptionStatus | undefined): {
  title: string;
  subtitle: string;
} {
  switch (status) {
    case "canceled":
    case "canceling":
      return {
        title: "Your subscription has ended",
        subtitle:
          "Resubscribe below to restore access to your dashboard, organizations, and premium features.",
      };
    case "past_due":
      return {
        title: "Payment required",
        subtitle:
          "Your subscription payment could not be processed. Update your payment method to continue.",
      };
    default:
      return {
        title: "Premium access required",
        subtitle:
          "Subscribe to unlock your dashboard, organizations, server listings, and more.",
      };
  }
}

// ─── Plan card sub-component ──────────────────────────────────────────────────

interface PlanCardProps {
  plan: (typeof PRICING_PLANS)[number];
  returnPath: string;
}

function PlanCard({ plan, returnPath }: PlanCardProps) {
  const { startCheckout, loadingPlan, error } = useCheckout();
  const isLoading = loadingPlan === plan.id;

  async function handleSubscribe() {
    await startCheckout(plan.id as PricingPlanId, {
      successRedirect: returnPath,
    });
  }

  return (
    <div
      className={[
        "relative flex flex-col rounded-xl border p-6 transition-shadow",
        plan.badge
          ? "border-primary bg-primary/5 shadow-md"
          : "border-border bg-card",
      ].join(" ")}
    >
      {plan.badge && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-primary-foreground">
          {plan.badge}
        </span>
      )}

      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground">{plan.name}</h3>
        <p className="mt-0.5 text-sm text-muted">{plan.description}</p>
      </div>

      <div className="mb-5">
        <span className="text-3xl font-bold text-foreground">
          ${plan.priceMonthly.toFixed(2)}
        </span>
        <span className="ml-1 text-sm text-muted">/month</span>
      </div>

      <ul className="mb-6 space-y-2 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-foreground/80">
            <svg
              viewBox="0 0 16 16"
              fill="currentColor"
              className="mt-0.5 h-4 w-4 shrink-0 text-primary"
              aria-hidden="true"
            >
              <path d="M13.28 4.22a.75.75 0 0 1 0 1.06l-7 7a.75.75 0 0 1-1.06 0l-3.5-3.5a.75.75 0 1 1 1.06-1.06L5.75 10.69l6.47-6.47a.75.75 0 0 1 1.06 0Z" />
            </svg>
            {f}
          </li>
        ))}
      </ul>

      {error && !isLoading && (
        <p className="mb-3 text-xs text-red-500">{error}</p>
      )}

      <button
        onClick={handleSubscribe}
        disabled={isLoading}
        className={[
          "w-full rounded-lg py-2.5 text-sm font-semibold transition-colors",
          plan.badge
            ? "bg-primary text-primary-foreground hover:bg-primary/90"
            : "bg-foreground/5 text-foreground hover:bg-foreground/10",
          isLoading ? "cursor-wait opacity-70" : "",
        ].join(" ")}
      >
        {isLoading ? "Opening checkout…" : "Subscribe Now"}
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * SubscriptionExpiredUpsell
 *
 * Graceful degradation UI shown inline (within the premium route) when a
 * signed-in user's subscription has expired, been cancelled, or is past-due.
 *
 * Instead of redirecting away to /pricing, the SubscriptionGate renders this
 * component in place of the gated content. The user sees:
 *   1. A clear headline explaining why their access was revoked
 *   2. The date their access ended (when available)
 *   3. Pricing plan cards with direct Polar.sh checkout CTAs
 *   4. A fallback link to /pricing for more detail
 *
 * This keeps the user contextually informed ("you were here, subscribe to
 * continue") rather than cold-redirecting them to the marketing pricing page.
 *
 * Used by: SubscriptionGate (when status is not auth_required / tier_insufficient)
 */
export function SubscriptionExpiredUpsell({
  status,
  expiredAt,
  returnPath = "/dashboard",
}: SubscriptionExpiredUpsellProps) {
  const { title, subtitle } = headlineForStatus(status);

  // Only show the Founder series (starter / plus / pro) in the upsell.
  // Server slot plans (basic, pro_server, enterprise) are excluded here —
  // those are billed separately and users typically navigate to /pricing for them.
  const founderPlans = PRICING_PLANS.filter((p) =>
    ["starter", "plus", "pro"].includes(p.id)
  );

  return (
    <div className="flex min-h-[70vh] flex-col items-center px-4 py-16">
      {/* ── Status icon ────────────────────────────────────────────────── */}
      <div className="mb-6 rounded-full bg-foreground/5 p-5">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          className="h-10 w-10 text-muted"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4M12 16h.01" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {/* ── Headline ───────────────────────────────────────────────────── */}
      <h1 className="mb-2 text-2xl font-bold text-foreground text-center">{title}</h1>
      <p className="mb-2 max-w-md text-center text-sm text-muted">{subtitle}</p>

      {expiredAt && (
        <p className="mb-8 text-xs text-muted/70">
          Access ended {formatDate(expiredAt)}.
        </p>
      )}

      {!expiredAt && <div className="mb-8" />}

      {/* ── Plan cards ─────────────────────────────────────────────────── */}
      <div className="w-full max-w-4xl">
        <div className="grid gap-6 sm:grid-cols-3">
          {founderPlans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} returnPath={returnPath} />
          ))}
        </div>
      </div>

      {/* ── Fallback link ──────────────────────────────────────────────── */}
      <p className="mt-8 text-center text-sm text-muted">
        Want to compare plans in detail?{" "}
        <Link
          to={`/pricing?source=subscription_expired&next=${encodeURIComponent(returnPath)}`}
          className="underline underline-offset-2 hover:text-foreground transition-colors"
        >
          View pricing page
        </Link>
      </p>
    </div>
  );
}
